import { formatISO } from 'date-fns';

import { getDestinationMetadata } from '@/constants/destinationsMetadata';
import { useCurrencyRevalidator } from '@/hooks/useCurrencyRevalidator';
import { useDeviceOS } from '@/hooks/useDeviceOS';
import PlanSelection from '@/pages/PlanSelection';
import { getVisitorOrigin } from '@/server-only/visitorOrigin';
import { getConfig } from '@/services/api';
import { getTestimonials } from '@/services/cms';
import {
  getGlobalAppRatingsContent,
  getGlobalFeaturedInCompaniesContent,
  getGlobalPlanSelectionsSocialProofsContent,
  getGlobalPriceMatchBannerContent,
  getHelpCentreContent,
  getHowDoesItWorkContent,
} from '@/services/cms/CmsServices';
import { getPlanSelectionCountries } from '@/services/PlanSelectionService/PlanSelectionsService';
import { getProductOfferings } from '@/services/product/ProductOfferingService';
import {
  getAllCountries,
  getFeaturedCountries,
} from '@/services/resources/ResourceService';
import { getCountryUtils } from '@/utils/countryUtils';
import { digestAppRatings } from '@/utils/digestAppRatings';
import { getMediaUrlForSize } from '@/utils/mediaUtils';
import { findMetaMatch, MetaStack } from '@/utils/metaUtils';
import { isMobileApp } from '@/utils/platformUtils';
import { getCurrency, getServerUserAgent } from '@/utils/ssrUtils';
import { getLayoutPlans, hasTestimonials } from '~/helpers/pageLayoutData';
import { getI18nInstance, getLocale } from '~/middleware/i18next';

import { getSessionId } from '../../src/server-only/sessions';
import { Route } from './+types/plan-selection';

export const loader = async ({
  context,
  params,
  request,
}: Route.LoaderArgs) => {
  const visitorOrigin = await getVisitorOrigin(request);
  const sessionId = await getSessionId(request);
  const { countrySlug } = params;
  if (!countrySlug) {
    throw new Response('Not Found', { status: 404 });
  }

  const { t } = getI18nInstance(context);
  const { getCountryNameTranslation } = getCountryUtils(t);

  const config = await getConfig({ sessionId, visitorOrigin });
  const currency = getCurrency({ request, config });
  const metadata = getDestinationMetadata(t, countrySlug);
  const locale = getLocale(context);

  const [
    countries,
    products,
    cmsContent,
    featuredCountries,
    helpCentre,
    howDoesItWork,
    cmsAppRatings,
    featuredCompaniesContent,
    planSelectionsSocialProofs,
    priceMatchBanner,
    testimonials,
  ] = await Promise.all([
    getAllCountries(sessionId, visitorOrigin),
    getProductOfferings({
      sessionId,
      country_slug: countrySlug,
      travel_date: formatISO(new Date(), { representation: 'date' }),
      currency: getCurrency({ request, config }),
      locale: params.lang,
      visitorOrigin,
    }),
    getPlanSelectionCountries({
      sessionId,
      countrySlug,
      locale: params.lang,
    }),
    getFeaturedCountries(sessionId, currency, visitorOrigin),
    getHelpCentreContent({ sessionId, locale: params.lang }),
    getHowDoesItWorkContent({ sessionId, locale: params.lang }),
    getGlobalAppRatingsContent(sessionId),
    getGlobalFeaturedInCompaniesContent({ sessionId, locale: params.lang }),
    getGlobalPlanSelectionsSocialProofsContent(sessionId, params.lang),
    getGlobalPriceMatchBannerContent({
      locale: params.lang,
      sessionId,
    }),
    getTestimonials({ sessionId, locale: params.lang }),
  ]);

  const currentCountry = countries.find(
    (country) => countrySlug === country.slug,
  );

  if (!currentCountry) {
    throw new Response(`${countrySlug} Not Found`, { status: 404 });
  }

  const title =
    cmsContent?.meta?.title ??
    metadata?.title ??
    t('browser.planSelectionTitle', 'eSIM {{country}}', {
      country: getCountryNameTranslation(currentCountry.description),
    });

  const description =
    cmsContent?.meta?.description ??
    metadata?.description ??
    t(
      'browser.planSelectionDescription',
      'Avoid roaming charges in {{country}} with Sim Local. Great value local & regional Travel eSIMs to keep you connected wherever you are.',
      {
        country: getCountryNameTranslation(currentCountry.description),
      },
    );

  const image = cmsContent?.meta?.image;

  const structuredData = cmsContent?.meta?.structuredData;

  const plans = await getLayoutPlans({
    sessionId,
    pageLayout: cmsContent?.layout,
    currency,
    locale: params.lang,
    visitorOrigin,
  });

  return {
    countries,
    currentCountry,
    products,
    helpCentre,
    howDoesItWork,
    cmsContent,
    featuredCountries,
    plans,
    testimonials:
      cmsContent?.layout && hasTestimonials(cmsContent.layout)
        ? testimonials
        : undefined,
    planSelectionsSocialProofs,
    meta: {
      title,
      description,
      image,
      structuredData,
    },
    userAgent: getServerUserAgent(request),
    featuredCompaniesContent,
    cmsAppRatings,
    priceMatchBanner: cmsContent?.isPriceMatchBannerEnabled
      ? priceMatchBanner
      : undefined,
    locale,
  };
};

export const meta: Route.MetaFunction = ({ matches, data }: Route.MetaArgs) => {
  if (!data) return [];

  const rootMatch = findMetaMatch('root', matches);
  const metaStack = new MetaStack(rootMatch.meta);

  const { meta } = data;
  const { title, description, image, structuredData } = meta;

  metaStack.title({ title, brandName: rootMatch.data.config.BRAND_NAME });
  metaStack.description(description);

  const imageUrl = image ? getMediaUrlForSize(image, 'w1080') : undefined;
  if (imageUrl) {
    metaStack.push({
      property: 'og:image',
      content: imageUrl,
    });
  }

  if (structuredData) {
    structuredData.forEach(({ jsonld }) => {
      metaStack.push({
        'script:ld+json': jsonld,
      });
    });
  }

  return metaStack.get();
};

export default function RouteComponent({ loaderData }: Route.ComponentProps) {
  const {
    countries,
    currentCountry,
    products,
    helpCentre,
    howDoesItWork,
    cmsContent,
    featuredCountries,
    userAgent,
    testimonials,
    plans,
    cmsAppRatings,
    planSelectionsSocialProofs,
    priceMatchBanner,
    featuredCompaniesContent,
    locale,
  } = loaderData;

  useCurrencyRevalidator();
  const { deviceOS } = useDeviceOS();

  const appRatings = digestAppRatings({
    deviceOS,
    cmsAppRatings,
    isMobileApp,
  });

  return (
    <PlanSelection
      plans={plans}
      testimonials={testimonials}
      helpCentre={helpCentre}
      howDoesItWork={howDoesItWork}
      featuredCountries={featuredCountries}
      countries={countries}
      currentCountry={currentCountry}
      products={products}
      cmsContent={cmsContent}
      userAgent={userAgent}
      featuredCompaniesContent={featuredCompaniesContent}
      appRatings={appRatings}
      planSelectionsSocialProofs={planSelectionsSocialProofs}
      priceMatchBanner={priceMatchBanner}
      locale={locale}
    />
  );
}
